import React, { useRef } from "react";
import { Line } from "react-chartjs-2";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

const utahData = "/img/headerlogo.png";
const kpi = "/img/KPI-DASH 2.png";
const bodex = "/img/bodexLogo.png";
const pdf = "/img/pdf1.png";

const EngTable5 = ({ data }) => {
  const pdfRef = useRef();

  // ---- Compute Insights ----
  const peak = data.reduce(
    (max, row) => (row.all_sexes > max.all_sexes ? row : max),
    data[0]
  );

  const first = data[0].all_sexes;
  const last = data[data.length - 1].all_sexes;
  const change = last - first;
  const growthPercent = ((change / first) * 100).toFixed(1);

  // ---- Chart Config ----
  const chartData = {
    labels: data.map((item) => item.year),
    datasets: [
      {
        label: "Total Employment (All Sexes)",
        data: data.map((item) => parseInt(item.all_sexes)),
        borderColor: "#ffffbb",
        backgroundColor: "#ffffbb80",
        borderWidth: 3,
        tension: 0.4,
        pointBackgroundColor: "white",
        pointBorderColor: "#ffffbb",
        pointBorderWidth: 2,
        pointRadius: 6,
        pointHoverRadius: 8,
        fill: true,
      },
    ],
  };

  const options = {
    responsive: true,
    plugins: {
      legend: { position: "top" },
      title: {
        display: true,
        text: "📊 Total Workforce Trend (All Sexes)",
        font: { size: 16, weight: "bold" },
      },
    },
    scales: {
      y: {
        beginAtZero: false,
        title: { display: true, text: "Employment Count", font: { size: 14, weight: "bold" } },
      },
      x: {
        title: {
          display: true,
          text: "Year",
          font: { size: 14, weight: "bold" },
        },
      },
    },
  };

  // ---- PDF Export ----
  const downloadPDF = () => {
    const element = pdfRef.current;

    html2canvas(element, {
      scale: 2,
      useCORS: true,
      backgroundColor: "#ffffff",
      scrollY: -window.scrollY,
    }).then((canvas) => {
      const imgData = canvas.toDataURL("image/png");
      const pdfFile = new jsPDF("p", "mm", "a4");

      const contentWidth = pdfFile.internal.pageSize.getWidth() - 10;
      const imgHeight = (canvas.height * contentWidth) / canvas.width;

      // Header
      pdfFile.addImage(utahData, "PNG", 60, 6, 30, 18);
      pdfFile.setFontSize(22);
      pdfFile.text("UTahData.org", 100, 20);

      pdfFile.line(10, 32, 200, 32);

      // Chart/Table
      pdfFile.addImage(imgData, "PNG", 5, 35, contentWidth, imgHeight);

      pdfFile.line(10, 260, 200, 260);

      // Footer
      pdfFile.addImage(utahData, "PNG", 22, 265, 30, 15);
      pdfFile.setFontSize(10);
      pdfFile.text("Built by", 55, 275);
      pdfFile.addImage(bodex, "PNG", 68, 269, 44, 10);
      pdfFile.text("using", 112, 275);
      pdfFile.addImage(kpi, "PNG", 125, 265, 20, 20);

      pdfFile.text("20250916.v3.198", 148, 275);
      pdfFile.text(`Downloaded: ${new Date().toLocaleDateString()}`, 150, 295);
      pdfFile.text(`From: ${window.location.href}`, 10, 295);

      pdfFile.save("total_employment_all_sexes.pdf");
    });
  };

  return (
    <>
      <div ref={pdfRef} className="p-6 bg-white rounded-xl shadow-lg">
        <h2 className="text-2xl font-bold text-center text-gray-800 mb-6">
          Total Employment Overview (All Sexes)
        </h2>

        {/* Insight Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="p-4 text-center bg-green-100 rounded-xl shadow">
            <p className="text-sm text-gray-600 font-medium">Overall Growth</p>
            <p className="text-2xl font-bold text-green-800">
              +{growthPercent}%
            </p>
          </div>

          <div className="p-4 text-center bg-blue-100 rounded-xl shadow">
            <p className="text-sm text-gray-600 font-medium">
              Peak Workforce Year
            </p>
            <p className="text-2xl font-bold text-blue-800">{peak.year}</p>
          </div>

          <div className="p-4 text-center bg-yellow-100 rounded-xl shadow">
            <p className="text-sm text-gray-600 font-medium">
              Latest Registered Value
            </p>
            <p className="text-2xl font-bold text-yellow-800">
              {Number(last).toLocaleString("en-US")}
            </p>
          </div>
        </div>

        {/* Table + Chart Layout */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Table */}
          <div className="flex flex-col justify-center">
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-center shadow-md rounded-lg overflow-hidden">
                <thead className="bg-gray-800 text-white">
                  <tr>
                    <th className="p-2 text-left">Year</th>
                    <th className="p-2" style={{ textAlign: 'right' }}>Total Employment</th>
                  </tr>
                </thead>
                <tbody>
                  {data.map((row) => (
                    <tr
                      key={row.year}
                      className={`${row.year === peak.year ? "bg-green-100 font-bold" : "hover:bg-gray-100"}`}
                    >
                      <td className="p-2 text-left">{row.year}</td>
                      <td className="p-2" style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{Number(row.all_sexes).toLocaleString("en-US")}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Chart */}
          <div className="flex flex-col w-full overflow-x-auto bg-white rounded-lg shadow-md">
            <div className="min-w-[400px] w-full h-96 p-4">
              <Line data={chartData} options={options} />
            </div>
          </div>
        </div>
      </div>

      {/* PDF Button */}
      <div className="flex justify-center mt-4">
        <button
          onClick={downloadPDF}
          className="bg-red-700 hover:bg-red-900 text-white px-4 py-2 rounded-lg flex items-center gap-2 shadow-lg transition-transform hover:scale-105"
        >
          Download as PDF
          <img src={pdf} className="w-[1.5rem]" alt="Download icon" />
        </button>
      </div>
    </>
  );
};

export default EngTable5;
